import { produce } from 'immer';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '../../../../../common/global/helpers';
import { LoadingState } from '../../../../../common/global/models';
import useMount from '../../../../../common/hooks/useMount';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import Switch from '../../../../../common/widgets/Switch';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { OptInSourceTypes, OptInTypes } from '../../../enums';
import { resources } from '../../../resources/resources';
import { dynamicYieldService } from '../../../services/dynamic-yield';
import { optInService } from '../../../services/opt-in';
import applicationCrumbs from '../../ApplicationCrumbs';
import PlatformInformation from '../../Common/PlatformInformation';
import MyAccountMenu from '../Common/MyAccountMenu';

type PreferencesPageState = {
  newsletter: boolean;
  availabilityNotification: boolean;
  dyConsentAccepted: boolean;
  saveButtonState: LoadingState;
};

export const PreferencesPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const [state, setState] = useState<PreferencesPageState | null>(null);
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const { customerInfo, setCustomerInfo } = useContext(CustomerInfoContext);

  const loadPreferences = async (abortController?: AbortController) => {
    const response = await optInService.preferences({
      signal: abortController?.signal,
    });

    if (!response.hasErrors) {
      setState({
        ...response.data,
        saveButtonState: LoadingState.Ready,
      });
    } else {
      if (!abortController?.signal.aborted) {
        setShowErrorPanel(true);
      }
    }
  };

  const updateState = (optInType: OptInTypes, value: boolean) => {
    setState(
      produce(draft => {
        switch (optInType) {
          case OptInTypes.AvailabilityNotification:
            draft!.availabilityNotification = value;
            break;
          case OptInTypes.NewsletterMarketing:
            draft!.newsletter = value;
            setCustomerInfo(
              produce(customerInfo, draft => {
                draft.showNewsletterBanners = !value;
              }),
            );
            break;
          case OptInTypes.DynamicYield:
            draft!.dyConsentAccepted = value;
            break;
          default:
            break;
        }
      }),
    );
  };

  const updatePreference = async (optInType: OptInTypes, value: boolean) => {
    updateState(optInType, value);
    const response = await optInService.consent(
      optInType,
      value,
      OptInSourceTypes.CommunicationPreferences,
    );
    if (optInType === OptInTypes.DynamicYield && value) {
      dynamicYieldService.setDyId();
    }
    if (response.hasErrors) {
      updateState(optInType, !value);
      showErrorToast(translate(resources.errors.generic));
    }
  };

  useMount(loadPreferences);

  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, crumbs.accountDashboard, crumbs.preferences]}
      />
      <section className="section-main section-actions my-account-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="page-actions">
                <h1>{translate(resources.preferences.title)}</h1>
              </div>
              <div className="preferences-content">
                {/* Top content */}
                {/* Preferences switches */}
                <div className="row">
                  <div className="col col-12 col-md-12">
                    <Loader
                      loading={!state}
                      loader={<LoadingSpinner />}
                      showErrorPanel={showErrorPanel}
                    >
                      {!!state && (
                        <>
                          {customerInfo.allowsDy && (
                            <div className="preference-option">
                              <div className="preference-option-action">
                                <Switch
                                  checked={state.dyConsentAccepted}
                                  onChange={e =>
                                    updatePreference(
                                      OptInTypes.DynamicYield,
                                      e.value,
                                    )
                                  }
                                />
                              </div>
                              <div className="preference-option-info">
                                <div>
                                  {translate(
                                    resources.preferences.dyConsentAccepted
                                      .label,
                                  )}
                                </div>
                                <span>
                                  {translate(
                                    resources.preferences.dyConsentAccepted
                                      .info,
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                          {customerInfo.showNewsletterPreferences &&
                            !customerInfo.isSavingsUser && (
                              <div className="preference-option">
                                <div className="preference-option-action">
                                  <Switch
                                    checked={state.newsletter}
                                    onChange={e =>
                                      updatePreference(
                                        OptInTypes.NewsletterMarketing,
                                        e.value,
                                      )
                                    }
                                  />
                                </div>
                                <div className="preference-option-info">
                                  <div>
                                    {translate(
                                      resources.preferences.newsletter.label,
                                    )}
                                  </div>
                                  <span>
                                    {translate(
                                      resources.preferences.newsletter.info,
                                    )}
                                  </span>
                                </div>
                              </div>
                            )}
                          {!customerInfo.isSavingsUser && (
                            <div className="preference-option">
                              <div className="preference-option-action">
                                <Switch
                                  checked={state.availabilityNotification}
                                  onChange={e =>
                                    updatePreference(
                                      OptInTypes.AvailabilityNotification,
                                      e.value,
                                    )
                                  }
                                />
                              </div>
                              <div className="preference-option-info">
                                <div>
                                  {translate(
                                    resources.preferences
                                      .availabilityNotification.label,
                                  )}
                                </div>
                                <span>
                                  {translate(
                                    resources.preferences
                                      .availabilityNotification.info,
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </Loader>
                  </div>
                </div>
                <hr></hr>

                {/* Preferences copy */}
                <div className="row">
                  <div className="col col-12 col-md-12">
                    <h3>{translate(resources.preferences.info.title)}</h3>
                    <div
                        dangerouslySetInnerHTML={{
                          __html: translate(resources.preferences.info.text1, {
                            0: 'https://benefit.edenred.ro/politica-de-prelucrare-a-datelor.pdf',
                          }),
                        }}
                      />
                      {!customerInfo?.isSavingsUser && <div
                        dangerouslySetInnerHTML={{
                          __html: translate(resources.preferences.info.text2, {
                            0: 'https://benefit.edenred.ro/exercitare-drepturi-gdpr',
                          }),
                        }}
                      />}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
